import {
  TermDto,
  TermHistoryEntryDto,
  UserDto,
} from '@act/shared/data-transfer-objects';
import { Dialog, DialogContent, Typography } from '@mui/material';
import { useCallback } from 'react';
import { Box, ChipColor, TaggableChip } from '@act/storybook';
import { GenericDialogProps } from 'utilities/dialog-context/DialogContextHelpers';
import ConsentHistoryEntry from './ConsentHistoryEntry';
import CloseableDialogTitle from './components/dialog-components/CloseableDialogTitle';

/** Props unique to the member consent history dialog. */
export interface ViewConsentsHistoryDialogUniqueProps {
  member: UserDto;
  terms?: TermDto[];
}

export type ViewConsentsHistoryCallbackInput = null;
export type ViewConsentsHistoryDialogProps = GenericDialogProps<null> &
  ViewConsentsHistoryDialogUniqueProps;

/**
 * Status chip for the current term row (Accepted / Rejected / Incomplete).
 *
 * @param term - Term whose current `consented` value is shown
 * @returns Colored status chip
 */
function getTermChip(term: TermDto): JSX.Element {
  let label = 'Incomplete';
  let type: ChipColor = 'warning';

  if (term.consented === true) {
    label = 'Accepted';
    type = 'success';
  } else if (term.consented === false) {
    label = 'Rejected';
    type = 'error';
  }
  return <TaggableChip label={label} color={type} isTag />;
}

/**
 * Renders history entries that include a submission.
 *
 * @param history - Same-term and same-key predecessor signatures
 * @returns History rows, omitting entries without submission metadata
 */
function renderHistoryEntries(history: TermHistoryEntryDto[] | undefined) {
  return (history ?? []).map(entry =>
    entry.termResponseSubmission ? (
      <ConsentHistoryEntry
        key={entry.id}
        consented={entry.consented}
        termResponseSubmission={entry.termResponseSubmission}
      />
    ) : null
  );
}

/**
 * Dialog listing a member's current consent status and prior signatures.
 *
 * @param member - Member whose consents are shown
 * @param terms - Current-group and history-only terms (with `history` from the API)
 * @param open - Whether the dialog is visible
 * @param setOpen - Closes the dialog
 */
export const ViewConsentsHistoryDialog = ({
  member,
  terms,
  open,
  setOpen,
}: ViewConsentsHistoryDialogProps) => {
  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm">
      <CloseableDialogTitle
        title={`${member?.firstName}'s consents`}
        handleClose={handleClose}
      />
      <DialogContent className="view-consents-history-dialog-content">
        {!terms?.length && <Typography>No record found</Typography>}
        {!!terms?.length && (
          <Box display="flex" flexDirection="column" gap="28px">
            {terms.map(term => {
              const hasCurrentSubmission =
                term.consented !== null &&
                term.consented !== undefined &&
                !!term.termResponseSubmission;
              const hasHistory = !!term.history?.length;

              return (
                <Box key={term.id}>
                  <Box
                    display="flex"
                    flexDirection="row"
                    gap="8px"
                    alignItems="center"
                    marginBottom="12px"
                  >
                    <Typography variant="titleMedium">{term.name}</Typography>
                    {getTermChip(term)}
                    {term.historyOnly && (
                      <TaggableChip
                        label="Previous version"
                        color="neutral"
                        isTag
                      />
                    )}
                  </Box>
                  {term.historyOnly && (
                    <Typography variant="bodyMedium" color="text.secondary">
                      This term is no longer on the current consent form.
                      Signatures below are historical only.
                    </Typography>
                  )}
                  {term.consented == null && !term.historyOnly && (
                    <Typography variant="bodyMedium">{`${term.name} has not yet been accepted or rejected.`}</Typography>
                  )}
                  {(hasCurrentSubmission || hasHistory) && (
                    <Box display="flex" flexDirection="column" gap="8px">
                      {hasCurrentSubmission && (
                        <ConsentHistoryEntry
                          consented={term.consented}
                          termResponseSubmission={term.termResponseSubmission}
                        />
                      )}
                      {renderHistoryEntries(term.history)}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ViewConsentsHistoryDialog;
